import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface PageHeaderValue {
  subtitle?: ReactNode;
  actions?: ReactNode;
}
interface PageHeaderCtx extends PageHeaderValue {
  set: (v: PageHeaderValue) => void;
  clear: () => void;
}

const Ctx = createContext<PageHeaderCtx | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [v, setV] = useState<PageHeaderValue>({});
  return (
    <Ctx.Provider value={{ ...v, set: setV, clear: () => setV({}) }}>
      {children}
    </Ctx.Provider>
  );
}

// Read the published header — used by the Topbar. Returns empty defaults when
// no PageHeaderProvider is mounted (so a bare Topbar still renders).
// eslint-disable-next-line react-refresh/only-export-components
export function usePageHeaderState(): PageHeaderValue {
  const ctx = useContext(Ctx);
  return ctx ? { subtitle: ctx.subtitle, actions: ctx.actions } : {};
}

// Pages call this to publish their subtitle/actions into the Topbar, and the
// header clears on unmount so a route without one resets to the Topbar defaults.
//
// IMPORTANT: the effect depends ONLY on `subtitle` (a primitive — the
// count string), NOT on `actions`. `actions` is JSX with a fresh identity every
// render; including it in deps would re-run the effect → set() → re-render →
// new JSX → re-run … an infinite loop (it would OOM tests AND spin in prod).
// `actions` is captured by the effect closure and re-published whenever subtitle
// changes; pages keep their action node effectively static, so this is correct.
// eslint-disable-next-line react-refresh/only-export-components
export function usePageHeader(v: PageHeaderValue): void {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePageHeader must be used inside <PageHeaderProvider>');
  const { set, clear } = ctx;
  const subtitle = v.subtitle;
  // Keep the latest actions in a ref (updated every render, no effect → no
  // loop) so the effect always publishes the current node, never the one
  // captured at the last subtitle change. NB actions still only re-publish when
  // subtitle changes — pages with truly reactive actions should vary subtitle
  // too; today's pages have static action nodes.
  const actionsRef = useRef(v.actions);
  actionsRef.current = v.actions;
  useEffect(() => {
    set({ subtitle, actions: actionsRef.current });
    return () => clear();
    // actions intentionally omitted from deps — see note above. set/clear are
    // stable (provider-level useState setter / identical-closure reset).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtitle]);
}
